import _ from "lodash";
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { connect } from "react-redux";

import { resourceLibrarySourceOrg } from "config";
import { graphql, compose } from "lib/apolloClient";
import {
  getWorkbookTemplateQueryV2,
  getWorkbookSchoolTemplateQuery,
  getWorkbookSchoolTemplatesCountQuery,
} from "./modules/WorkbookTemplateQueries";
import {
  getWorkbookTemplateQuery,
  getWorkbookSchoolTemplateQueryV2,
  getWorkbookSchoolTemplatesCountQueryV2,
} from "./modules/WorkbookTemplateQueriesV2";
import {
  getWorkbookTemplatesFromCache,
  getWorkbookSchoolTemplateFromCache,
  getWorkbookSchoolTemplateCountFromCache,
} from "./modules/WorkbookTemplateGraphqlHelpers";
import { deleteWorkbookTemplate } from "./modules/WorkbookTemplateModule";
import { isNewTemplateResolverEnabled } from "./modules/Utils";
import {
  getOrganizationId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import ACLStore from "lib/aclStore";

import {
  NoSearchInFilterIllustration,
  NoWorkbookTemplatesIllustration,
  NoFilterResultIllustration,
} from "@toddle-edu/ds-theme";
import {
  CloseOutlined,
  ChevronLeftOutlined,
  SchoolOutlined,
  EnglishOutlined,
  TeacherResourceOutlined,
  MathematicsOutlined,
  ScienceOutlined,
  CommentMultipleOutlined,
  WorkbookOutlined,
  AddOutlined,
} from "@toddle-edu/ds-icons";
import {
  Button,
  SearchInput,
  IconButton,
  SideNavigation,
  EmptyState,
} from "@toddle-edu/ds-web";

import Workbook from "AppComponents/Workbook";
import Loading from "UIComponents/Loading";
import { prepareLoadMoreFeed } from "Utils";

import { useI18n } from "Hooks";

import BlankWorkbookCard from "./components/BlankWorkbookCard";
import CarouselCardsV2 from "./components/CarouselCardsV2";
import SchoolTemplateFilters from "./components/SchoolTemplateFilters";

import classes from "./WorkbookTemplateV2.scss";
import classNames from "classnames";

const ICON_MAPPING = [
  {
    keyword: "english",
    component: <EnglishOutlined variant="subtle" />,
  },
  {
    keyword: "science",
    component: <ScienceOutlined variant="subtle" />,
  },
  {
    keyword: "graphic",
    component: <TeacherResourceOutlined variant="subtle" />,
  },
  {
    keyword: "math",
    component: <MathematicsOutlined variant="subtle" />,
  },
  {
    keyword: "visible thinking",
    component: <CommentMultipleOutlined variant="subtle" />,
  },
];

const styles = {
  titleStyle: {
    fontSize: "16px",
  },
  subtitleStyle: {
    fontSize: "14px",
  },
};

const WorkbookTemplateV2 = React.memo(props => {
  const {
    closeModal,
    templateData,
    schoolTemplateData,
    filteredSchoolTemplateData,
    onUpdateSuccessfully,
    updateAttachments,
    isLoading,
    isData,
    isCreateBlankWorkbookAttachment,
    updateWorkbookTemplateFilters,
    searchText,
    folderId,
    createBlankWorkbook,
    handleCreateAttachment,
    handleEditAttachment,
    staffList,
    createdBy,
    setCreatedBy,
    enableWorkbookTemplateLibrary,
    isAnySchoolTemplateExist,
  } = props;

  const [selectedOption, setSelectedOption] = useState(folderId);
  const [templateLabel, setTemplateLabel] = useState("");
  const [isTemplateCreator, setIsTemplateCreator] = useState(false);
  const [showWorkbookPreview, setShowWorkbookPreview] = useState(false);
  const [selectedFolderLabel, setSelectedFolderLabel] = useState(
    _.isEqual(folderId, "schoolTemplate")
      ? "School templates"
      : _.get(_.find(templateData, { id: folderId }), "label", "")
  );
  const [templateAttachment, setTemplateAttachment] = useState({});
  const [resourceId, setResourceId] = useState("");
  const [searchInput, setSearchInput] = useState(searchText);
  const [previewLoading, setPreviewLoading] = useState(false);

  const scrollRef = useRef(null);
  const parentRef = useRef(null);

  const { t } = useI18n();

  useEffect(() => {
    if (
      isCreateBlankWorkbookAttachment &&
      ACLStore.can("FeatureFlag:WorkbookTemplatesCreateBlankDebug") &&
      !isLoading &&
      !isData
    ) {
      createBlankWorkbook();
    }
  }, [isLoading, isData, isCreateBlankWorkbookAttachment, createBlankWorkbook]);

  const resetScroll = useCallback(() => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [scrollRef]);

  const updateSearchText = _.debounce(val => {
    updateWorkbookTemplateFilters({
      key: "searchText",
      value: val,
    });
  }, 500);

  const updateFolderId = useCallback(
    folderId => {
      resetScroll();
      setSelectedOption(folderId);
      const folderLabel = _.isEqual(folderId, "schoolTemplate")
        ? "School templates"
        : _.get(_.find(templateData, { id: folderId }), "label", "");

      setSelectedFolderLabel(folderLabel);
      updateWorkbookTemplateFilters({ key: "folderId", value: folderId });
    },
    [resetScroll, updateWorkbookTemplateFilters, templateData]
  );

  const onUseTemplateClick = ({ id, label, isTemplateCreator, attachment }) => {
    setTemplateLabel(label);
    setIsTemplateCreator(isTemplateCreator);
    setTemplateAttachment(attachment);
    setResourceId(id);
    handleCreateAttachment({ attachment, isTemplateCreator, resourceId: id });
  };

  const onPreviewTemplateClick = ({
    label,
    isTemplateCreator,
    attachment,
    id,
  }) => {
    setShowWorkbookPreview(true);
    setTemplateLabel(label);
    setIsTemplateCreator(isTemplateCreator);
    setResourceId(id);
    setTemplateAttachment(attachment);
  };

  const onEditTemplateClick = ({ id, attachment }) => {
    handleEditAttachment({ attachment, resourceId: id });
  };

  const onDeleteTemplateClick = ({ id, label }) => {
    const {
      deleteWorkbookTemplate,
      organizationId,
      curriculumProgramId,
    } = props;
    deleteWorkbookTemplate({
      id,
      label,
      templateFilters: {
        ...schoolTemplateFilters,
        organizationId,
        searchText,
        curriculumProgramId,
      },
      schoolTemplateFilters: {
        ...schoolTemplateFilters,
        organizationId,
        curriculumProgramId,
        searchText,
        createdBy,
      },
    });
  };

  const checkIfTemplateHasData = () => {
    let hasData = false;
    let hasSearchData = false;

    _.map(templateData, template => {
      if (
        _.get(template, "childrenV2.resourceTypeBasedCount.totalCount", 0) > 0
      ) {
        hasSearchData = true;
        hasData = hasData || hasSearchData;
      }
    });

    if (
      _.get(
        schoolTemplateData,
        "childrenV2.resourceTypeBasedCount.totalCount",
        0
      ) > 0
    ) {
      hasSearchData = true;
      hasData = hasData || hasSearchData;
    }
    return hasData;
  };

  const sideBarOptions = useMemo(() => {
    const templateCountStyle = classNames("text-body-s", classes.templateCount);

    const getSchoolTemplatesSuffix = () => {
      const schoolTemplateCount = _.get(
        filteredSchoolTemplateData,
        "childrenV2.resourceTypeBasedCount.totalCount",
        -1
      );
      return (
        schoolTemplateCount > -1 && (
          <div className={templateCountStyle}>{schoolTemplateCount}</div>
        )
      );
    };

    const options = [
      // The root node of the hierarchy, containing top-level children nodes "1", "2", and "3"
      {
        key: "root",
        children: ["1", "2", "3"],
      },
      // Node "1" is a child of "root", with a tier level of 2 and children nodes "allTemplates" and "divider"
      {
        key: "1",
        tier: 2,
        parent: "root",
        children: ["allTemplates", "divider"],
      },
      // The "allTemplates" node contains a JSX title generated by getAllTemplateTitle function, is at tier level 1, and has no children
      {
        key: "allTemplates",
        title: t("workbook:all_templates"),
        tier: 1,
        parent: "1",
        children: [],
      },
      // Divider node used as a visual separator, unique key for this instance
      {
        key: "divider",
        isDivider: true,
      },
      // Node "2" is another child of "root", also at tier level 2, with children nodes "schoolTemplate" and "divider"
      {
        key: "2",
        tier: 2,
        parent: "root",
        children: ["schoolTemplate", "divider"],
      },
      // The "schoolTemplate" node contains a JSX title generated by getSchoolTemplatesTitle function, is at tier level 1, and has no children
      {
        key: "schoolTemplate",
        title: t("workbook:school_templates"),
        prefix: <SchoolOutlined variant="subtle" />,
        suffix: getSchoolTemplatesSuffix(),
        tier: 1,
        parent: "2",
        children: [],
      },
      // Divider node used as a visual separator, unique key for this instance
      {
        key: "divider",
        isDivider: true,
      },
      // Node "3" is another child of "root", it contains a static title "Toddle library" and has no children
      {
        key: "3",
        title: "Toddle library",
        tier: 2,
        parent: "root",
        children: [],
      },
    ];

    // Getting children array for static "Toddle library" option
    const rootChildrenArray = _.get(options, ["7", "children"]);

    const getIconFromLabel = label => {
      const match = _.find(ICON_MAPPING, ({ keyword }) =>
        _.includes(label, keyword)
      );
      return match ? (
        match.component
      ) : (
        <WorkbookOutlined variant="subtle" size="x-small" />
      );
    };

    _.forEach(templateData, template => {
      const { label, id } = template;
      const count = _.get(
        template,
        "childrenV2.resourceTypeBasedCount.totalCount",
        0
      );

      // pushing id in children array for static "Toddle library" option
      rootChildrenArray.push(id);

      // creating children options for static "Toddle library" option
      const option = {
        key: id,
        title: label,
        prefix: getIconFromLabel(_.toLower(label)),
        suffix: <div className={templateCountStyle}>{count}</div>,
        tier: 1,
        parent: "3",
        children: [],
      };

      // pushing above created option to options array
      options.push(option);
    });

    return options;
  }, [t, templateData, filteredSchoolTemplateData]);

  const handleEditOriginalTemplate = () => {
    handleEditAttachment({
      attachment: templateAttachment,
      resourceId,
    });
  };

  const handleUseTemplate = () => {
    handleCreateAttachment({
      attachment: templateAttachment,
      isTemplateCreator,
      resourceId,
    });
  };

  const handleSearchInput = val => {
    setSearchInput(val);
    updateSearchText(val.trim());
  };

  const handleBackFromPreview = () => {
    setShowWorkbookPreview(false);
    setTemplateLabel("");
    setIsTemplateCreator(false);
    setResourceId("");
    setTemplateAttachment({});
  };

  const invokeWorkbookCustomLoader = useCallback(({ isLoading }) => {
    setPreviewLoading(isLoading);
  }, []);

  const searchBoxClass = classNames(classes.searchBox, "text-body-1");
  const footerContainerStyles = classNames(classes.footerContainer, {
    [classes.footerRightContainer]: !isTemplateCreator,
  });
  const noSchoolTemplatesLabelStyle = classNames(
    "text-body-2",
    classes.noSchoolTemplatesLabel
  );

  const renderAllTemplates = () => {
    const allTemplateData = _.concat(schoolTemplateData, templateData);
    return _.map(
      _.filter(allTemplateData, template => {
        const templateEdges = _.get(template, "childrenV2.edges", []);
        const templateId = _.get(template, "id");
        return (
          !_.isEmpty(templateEdges) || _.isEqual(templateId, "schoolTemplate")
        );
      }),
      template => {
        const { id } = template;
        return (
          <CarouselCardsV2
            key={id}
            template={template}
            updateFolderId={updateFolderId}
            sideBarOptions={sideBarOptions}
            scrollRef={scrollRef}
            searchText={searchText}
            folderId={folderId}
            id={id}
            t={t}
            onUseTemplateClick={onUseTemplateClick}
            onPreviewTemplateClick={onPreviewTemplateClick}
            onEditTemplateClick={onEditTemplateClick}
            onDeleteTemplateClick={onDeleteTemplateClick}
          />
        );
      }
    );
  };

  const renderSelectedFolderTemplates = () => {
    const {
      networkStatus,
      isFetchingMoreData,
      isFilteredDataLoading,
      isFilteredData,
      hasNextPage,
      loadMoreEvents,
    } = props;

    const isSchoolTemplateSelected = _.isEqual(folderId, "schoolTemplate");

    const schoolTemplatesProps = isSchoolTemplateSelected
      ? {
          templateData: filteredSchoolTemplateData,
          isLoading: isFilteredDataLoading,
          isData: isFilteredData,
          networkStatus,
          isFetchingMoreData,
          hasNextPage,
          loadMoreEvents,
        }
      : {};

    return (
      <CarouselCardsV2
        folderLabel={selectedFolderLabel}
        sideBarOptions={sideBarOptions}
        scrollRef={scrollRef}
        searchText={searchText}
        folderId={folderId}
        t={t}
        onUseTemplateClick={onUseTemplateClick}
        onPreviewTemplateClick={onPreviewTemplateClick}
        onEditTemplateClick={onEditTemplateClick}
        onDeleteTemplateClick={onDeleteTemplateClick}
        staffList={staffList}
        createdBy={createdBy}
        setCreatedBy={setCreatedBy}
        {...schoolTemplatesProps}
      />
    );
  };

  const renderCards = () => {
    return _.isEqual(folderId, "allTemplates") ? (
      <div className={classes.templatesContainer}>
        {_.isEmpty(searchText) && (
          <BlankWorkbookCard createBlankWorkbook={createBlankWorkbook} />
        )}
        {renderAllTemplates()}
      </div>
    ) : (
      renderSelectedFolderTemplates()
    );
  };

  const renderSelectedFolderHeading = () => {
    const isSchoolTemplateSelected = _.isEqual(folderId, "schoolTemplate");
    return (
      isSchoolTemplateSelected && (
        <div className={classes.labelContainer}>
          <h6>{selectedFolderLabel}</h6>
          <SchoolTemplateFilters
            staffList={staffList}
            createdBy={createdBy}
            setCreatedBy={setCreatedBy}
          />
        </div>
      )
    );
  };

  const renderSchoolTemplatesHeadingWithCount = () => {
    const schoolTemplateCount = _.get(
      filteredSchoolTemplateData,
      "childrenV2.resourceTypeBasedCount.totalCount",
      -1
    );
    return (
      <div className={classes.schoolTemplateLabelContainer}>
        <h6>
          {schoolTemplateCount > -1
            ? `${t("workbook:school_templates")} (${schoolTemplateCount})`
            : t("workbook:school_templates")}
        </h6>
        <SchoolTemplateFilters
          staffList={staffList}
          createdBy={createdBy}
          setCreatedBy={setCreatedBy}
        />
      </div>
    );
  };

  const renderWorkbookPreview = () => (
    <div className={classes.previewContainer}>
      <Workbook
        shouldEnablePreload={false}
        imageOrientation={0}
        attachment={templateAttachment}
        workbookMode={"WORKBOOK_TEMPLATE_PREVIEW"}
        isFullScreen={false}
        invokeWorkbookCustomLoader={invokeWorkbookCustomLoader}
        onUpdateSuccessfully={onUpdateSuccessfully}
        onDiscardDirectly={closeModal}
        updateAttachments={updateAttachments}
        iframeIdSelector={"workbook-iframe-classroom-1"}
        parentRef={parentRef?.current}
        parentId={"workbook-iframe-classroom-2"}
        portalType={"RESOURCE_LIBRARY"}
      />
      {previewLoading && (
        <div className={classes.loadingContainer}>
          <Loading />
        </div>
      )}
    </div>
  );

  const renderHeaderSection = () => (
    <div className={classes.headerContainer}>
      <div className={classes.headerLeftContainer}>
        {showWorkbookPreview && (
          <IconButton
            dsVersion={"2.0"}
            icon={<ChevronLeftOutlined variant="default" />}
            size={"medium"}
            variant={"neutral"}
            type={"plain"}
            onClick={handleBackFromPreview}
          />
        )}
        <h6>
          {showWorkbookPreview ? templateLabel : t("workbook:choose_template")}
        </h6>
      </div>
      <div className={classes.headerRightContainer}>
        {showWorkbookPreview ||
          !(isAnySchoolTemplateExist || enableWorkbookTemplateLibrary) || (
            <div className={classes.headerSearch}>
              <SearchInput
                placeholder={`${t("common:search_for_with_label", {
                  label: t("common:template_plural"),
                })}`}
                onChange={handleSearchInput}
                value={searchInput}
                size={"medium"}
                dsVersion="2.0"
                focusSearchBySlash={true}
              />
            </div>
          )}
        <IconButton
          dsVersion={"2.0"}
          icon={<CloseOutlined variant="default" />}
          size={"medium"}
          variant={"neutral"}
          type={"plain"}
          onClick={closeModal}
        />
      </div>
    </div>
  );

  const renderEmptySchoolTemplateState = () => (
    <div className={classes.emptySchoolTemplatesContainer}>
      <EmptyState
        illustration={
          isAnySchoolTemplateExist
            ? NoFilterResultIllustration
            : NoWorkbookTemplatesIllustration
        }
        title={
          isAnySchoolTemplateExist
            ? t("workbook:no_templates_match_filters")
            : t("workbook:no_templates_created")
        }
        subtitle={
          isAnySchoolTemplateExist
            ? t("workbook:no_school_templates_match_filters")
            : t("workbook:school_not_added_any_templates")
        }
        primaryButton={
          (enableWorkbookTemplateLibrary ||
            !isAnySchoolTemplateExist ||
            !_.isEmpty(searchText)) && (
            <Button
              size="medium"
              variant="primary"
              dsVersion={"2.0"}
              icon={<AddOutlined />}
              onClick={createBlankWorkbook}
            >
              {t("workbook:blank_workbook")}
            </Button>
          )
        }
        titleStyle={styles.titleStyle}
        subtitleStyle={styles.subtitleStyle}
        dsVersion={"2.0"}
      />
    </div>
  );

  const renderEmptyState = () => (
    <div className={classes.emptySchoolTemplatesContainer}>
      <EmptyState
        illustration={NoSearchInFilterIllustration}
        title={t("common:noResultHeading")}
        subtitle={t("workbook:no_templates_matching_your_search")}
        titleStyle={styles.titleStyle}
        subtitleStyle={styles.subtitleStyle}
        dsVersion={"2.0"}
      />
    </div>
  );

  const renderSchoolTemplatesFolder = () => {
    const { isFilteredData, isFilteredDataLoading } = props;
    const isFilteredDataEmpty = _.isEmpty(
      _.get(filteredSchoolTemplateData, "childrenV2.edges", [])
    );

    return isFilteredDataLoading && !isFilteredData ? (
      <Loading />
    ) : !isFilteredDataEmpty ? (
      renderCards()
    ) : (
      renderEmptySchoolTemplateState()
    );
  };

  const renderTemplates = () =>
    isLoading && !isData ? (
      <Loading />
    ) : checkIfTemplateHasData() ? (
      renderCards()
    ) : (
      renderEmptyState()
    );

  const renderLoadingAndEmptyState = () => {
    const isSchoolTemplateSelected = _.isEqual(folderId, "schoolTemplate");

    return isSchoolTemplateSelected
      ? renderSchoolTemplatesFolder()
      : renderTemplates();
  };

  const renderShowingResultsForSearchText = () =>
    !_.isEmpty(searchText) && (
      <div className={searchBoxClass}>
        {t("common:search_result", {
          searchText,
        })}
      </div>
    );

  const renderFooterActionButtons = () =>
    showWorkbookPreview && (
      <div className={footerContainerStyles}>
        {isTemplateCreator && (
          <Button
            variant={"progressive"}
            dsVersion="2.0"
            type="inline"
            size={"medium"}
            onClick={handleEditOriginalTemplate}
            disabled={_.isEmpty(templateAttachment)}
          >
            {t("common:edit_original_template")}
          </Button>
        )}
        <Button
          variant={"progressive"}
          dsVersion="2.0"
          type="fill"
          size={"medium"}
          onClick={handleUseTemplate}
          disabled={_.isEmpty(templateAttachment)}
        >
          {t("classRoom:use_template")}
        </Button>
      </div>
    );

  const renderWorkbookTemplateLibrary = () => (
    <div className={classes.centerDataContainer}>
      <div className={classes.leftContainer}>
        <SideNavigation
          dsVersion="2.0"
          options={sideBarOptions}
          onClick={updateFolderId}
          selectedOptionKey={selectedOption}
        />
      </div>
      <div className={classes.bodyContainer} ref={scrollRef}>
        {renderShowingResultsForSearchText()}
        {renderSelectedFolderHeading()}
        {renderLoadingAndEmptyState()}
      </div>
    </div>
  );

  const renderSchoolTemplateData = () => {
    const {
      networkStatus,
      isFetchingMoreData,
      isFilteredDataLoading,
      isFilteredData,
      hasNextPage,
      loadMoreEvents,
    } = props;

    const schoolTemplatesProps = {
      templateData: filteredSchoolTemplateData,
      isLoading: isFilteredDataLoading,
      isData: isFilteredData,
      networkStatus,
      isFetchingMoreData,
      hasNextPage,
      loadMoreEvents,
    };

    const isFilteredDataEmpty = _.isEmpty(
      _.get(filteredSchoolTemplateData, "childrenV2.edges", [])
    );

    return isFilteredDataLoading && !isFilteredData ? (
      <Loading />
    ) : !isFilteredDataEmpty ? (
      <CarouselCardsV2
        folderLabel={selectedFolderLabel}
        scrollRef={scrollRef}
        folderId={"schoolTemplate"}
        t={t}
        onUseTemplateClick={onUseTemplateClick}
        onPreviewTemplateClick={onPreviewTemplateClick}
        onEditTemplateClick={onEditTemplateClick}
        onDeleteTemplateClick={onDeleteTemplateClick}
        {...schoolTemplatesProps}
      />
    ) : (
      renderEmptySchoolTemplateState()
    );
  };

  const renderSchoolTemplateLibrary = () => {
    const { isFilteredData, isFilteredDataLoading } = props;
    if (!isAnySchoolTemplateExist) {
      return (
        <div className={classes.bodyContainer}>
          {isFilteredDataLoading && !isFilteredData ? (
            <Loading />
          ) : (
            renderEmptySchoolTemplateState()
          )}
        </div>
      );
    }

    const showBlankWorkbookCard = _.isEmpty(searchText);

    return (
      <div className={classes.bodyContainer} ref={scrollRef}>
        {showBlankWorkbookCard && (
          <div>
            <BlankWorkbookCard createBlankWorkbook={createBlankWorkbook} />
            <div className={classes.horizontalSeparator} />
          </div>
        )}
        {renderShowingResultsForSearchText()}
        {renderSchoolTemplatesHeadingWithCount()}
        {renderSchoolTemplateData()}
      </div>
    );
  };

  return (
    <div className={classes.container}>
      {renderHeaderSection()}

      <div
        className={classes.centerContainer}
        ref={parentRef}
        id={"workbook-iframe-classroom-2"}
      >
        {showWorkbookPreview
          ? renderWorkbookPreview()
          : enableWorkbookTemplateLibrary
          ? renderWorkbookTemplateLibrary()
          : renderSchoolTemplateLibrary()}
      </div>

      {renderFooterActionButtons()}
    </div>
  );
});

WorkbookTemplateV2.displayName = "WorkbookTemplateV2";

const templateFilters = {
  id: _.get(resourceLibrarySourceOrg, "workbook"),
  types: ["WORKBOOK_TEMPLATE"],
  portalType: "RESOURCE_LIBRARY",
  first: 10,
  states: ["PUBLISHED"],
  createdBy: [],
};
const templateFiltersV2 = {
  organizationId: _.get(resourceLibrarySourceOrg, "workbook"),
  types: ["WORKBOOK_TEMPLATE"],
  portalType: "RESOURCE_LIBRARY",
  first: 10,
  states: ["PUBLISHED"],
  createdBy: [],
};

const schoolTemplateFilters = {
  types: ["WORKBOOK_TEMPLATE"],
  first: 24,
  createdBy: [],
};

const mapStateToProps = (state, ownProps) => {
  const searchText = _.get(ownProps, "searchText", "");
  const folderId = _.get(ownProps, "folderId", "");
  const createdBy = _.get(ownProps, "createdBy");
  const enableWorkbookTemplateLibrary = _.get(
    ownProps,
    "enableWorkbookTemplateLibrary",
    false
  );
  return {
    organizationId: getOrganizationId(state),
    curriculumProgramId: getSelectedCurriculumId(state),
    searchText,
    templateFilters: {
      ...(isNewTemplateResolverEnabled() ? templateFiltersV2 : templateFilters),
      searchText,
    },
    schoolTemplateFilters,
    createdBy,
    folderId,
    isLoading: false,
    isData: true,
    enableWorkbookTemplateLibrary,
  };
};

const mapActionCreators = {
  deleteWorkbookTemplate,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getWorkbookSchoolTemplateQuery, {
    name: "getWorkbookSchoolTemplate",
    skip: ({ enableWorkbookTemplateLibrary }) => {
      return isNewTemplateResolverEnabled() || !enableWorkbookTemplateLibrary;
    },
    options: ({
      schoolTemplateFilters,
      organizationId,
      curriculumProgramId,
      searchText,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ...schoolTemplateFilters,
        organizationId,
        curriculumProgramId,
        searchText,
      },
    }),
    props({ getWorkbookSchoolTemplate, ownProps: { isData, isLoading } }) {
      const data = getWorkbookSchoolTemplateFromCache(
        getWorkbookSchoolTemplate.variables
      );
      const templateData = _.get(
        data,
        "node.workbookTemplates.organizationResource[0]",
        {}
      );
      let schoolTemplateData;
      if (!_.isEmpty(data)) {
        schoolTemplateData = _.assign({}, templateData, {
          label: "School templates",
          id: "schoolTemplate",
        });
      }
      const networkStatus = _.get(getWorkbookSchoolTemplate, "networkStatus");
      return {
        schoolTemplateData,
        isData: !_.isEmpty(schoolTemplateData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getWorkbookSchoolTemplateQuery, {
    name: "getWorkbookSchoolFilteredTemplate",
    skip: () => isNewTemplateResolverEnabled(),
    options: ({
      schoolTemplateFilters,
      organizationId,
      curriculumProgramId,
      searchText,
      createdBy,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ...schoolTemplateFilters,
        organizationId,
        curriculumProgramId,
        searchText,
        createdBy,
      },
    }),
    props({
      getWorkbookSchoolFilteredTemplate,
      ownProps: { isData, isLoading },
    }) {
      const variables = _.get(getWorkbookSchoolFilteredTemplate, "variables");
      const data = getWorkbookSchoolTemplateFromCache(variables);
      const filteredSchoolTemplateData = _.get(
        data,
        "node.workbookTemplates.organizationResource[0]",
        []
      );
      const endCursor = _.get(
        filteredSchoolTemplateData,
        "childrenV2.pageInfo.endCursor",
        ""
      );
      const networkStatus = _.get(
        getWorkbookSchoolFilteredTemplate,
        "networkStatus"
      );
      return {
        filteredSchoolTemplateData,
        networkStatus,
        isFetchingMoreData: _.isEqual(networkStatus, 3),
        isFilteredDataLoading: _.includes([1, 2], networkStatus),
        isFilteredData: !_.isEmpty(filteredSchoolTemplateData),
        hasNextPage: _.get(
          filteredSchoolTemplateData,
          "childrenV2.pageInfo.hasNextPage",
          false
        ),
        loadMoreEvents: prepareLoadMoreFeed({
          variables: {
            ...schoolTemplateFilters,
            after: endCursor,
          },
          queryData: getWorkbookSchoolFilteredTemplate,
          customPathString:
            "node.workbookTemplates.organizationResource[0].childrenV2",
        }),
      };
    },
  }),
  graphql(getWorkbookTemplateQueryV2, {
    name: "getWorkbookTemplates",
    skip: ({ enableWorkbookTemplateLibrary }) => {
      return isNewTemplateResolverEnabled() || !enableWorkbookTemplateLibrary;
    },
    options: ({ templateFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ...templateFilters,
      },
    }),
    props({
      getWorkbookTemplates: { networkStatus },
      ownProps: { isData, isLoading, templateFilters },
    }) {
      const data = getWorkbookTemplatesFromCache({
        ...templateFilters,
        folderId: "allTemplates",
      });

      //removing Basic folder which contains Blank workbook
      const templateData = _.reject(
        _.get(data, "node.organizationResources", []),
        { label: "Basic" }
      );

      let isCreateBlankWorkbookAttachment = false;
      if (
        _.isEmpty(templateData) &&
        _.isEmpty(_.get(templateFilters, "searchText"))
      ) {
        isCreateBlankWorkbookAttachment = true;
      }

      return {
        templateData,
        isData: !_.isEmpty(data) && isData,
        isCreateBlankWorkbookAttachment,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getWorkbookSchoolTemplatesCountQuery, {
    name: "getWorkbookSchoolTemplatesCount",
    skip: () => isNewTemplateResolverEnabled(),
    options: ({ organizationId, curriculumProgramId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        types: ["WORKBOOK_TEMPLATE"],
        organizationId,
        curriculumProgramId,
      },
    }),
    props({
      getWorkbookSchoolTemplatesCount,
      ownProps: { isFilteredData, isFilteredDataLoading },
    }) {
      const variables = _.get(getWorkbookSchoolTemplatesCount, "variables");
      const data = getWorkbookSchoolTemplateCountFromCache(variables);
      const totalTemplatesCount = _.get(
        data,
        "node.workbookTemplates.organizationResource[0].childrenV2.resourceTypeBasedCount.totalCount",
        0
      );
      const isAnySchoolTemplateExist = !_.isEqual(totalTemplatesCount, 0);
      const networkStatus = _.get(
        getWorkbookSchoolTemplatesCount,
        "networkStatus"
      );
      return {
        isFilteredData: !_.isEmpty(data) && isFilteredData,
        isFilteredDataLoading:
          _.includes([1, 2], networkStatus) || isFilteredDataLoading,
        isAnySchoolTemplateExist,
      };
    },
  }),

  //new resolver queries
  graphql(getWorkbookSchoolTemplateQueryV2, {
    name: "getWorkbookSchoolTemplateV2",
    skip: ({ enableWorkbookTemplateLibrary }) => {
      return !isNewTemplateResolverEnabled() || !enableWorkbookTemplateLibrary;
    },
    options: ({
      schoolTemplateFilters,
      organizationId,
      curriculumProgramId,
      searchText,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ...schoolTemplateFilters,
        organizationId,
        curriculumProgramId,
        searchText,
      },
    }),
    props({ getWorkbookSchoolTemplateV2, ownProps: { isData, isLoading } }) {
      const data = getWorkbookSchoolTemplateFromCache(
        getWorkbookSchoolTemplateV2.variables
      );
      const templateData = _.get(
        data,
        "node.workbookTemplatesV2.workbookTemplateResource[0]",
        {}
      );
      let schoolTemplateData;
      if (!_.isEmpty(data)) {
        schoolTemplateData = _.assign({}, templateData, {
          label: "School templates",
          id: "schoolTemplate",
        });
      }
      const networkStatus = _.get(getWorkbookSchoolTemplateV2, "networkStatus");
      return {
        schoolTemplateData,
        isData: !_.isEmpty(schoolTemplateData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getWorkbookSchoolTemplateQueryV2, {
    name: "getWorkbookSchoolFilteredTemplateV2",
    skip: () => !isNewTemplateResolverEnabled(),
    options: ({
      schoolTemplateFilters,
      organizationId,
      curriculumProgramId,
      searchText,
      createdBy,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ...schoolTemplateFilters,
        organizationId,
        curriculumProgramId,
        searchText,
        createdBy,
      },
    }),
    props({
      getWorkbookSchoolFilteredTemplateV2,
      ownProps: { isData, isLoading },
    }) {
      const variables = _.get(getWorkbookSchoolFilteredTemplateV2, "variables");
      const data = getWorkbookSchoolTemplateFromCache(variables);
      const filteredSchoolTemplateData = _.get(
        data,
        "node.workbookTemplatesV2.workbookTemplateResource[0]",
        []
      );
      const endCursor = _.get(
        filteredSchoolTemplateData,
        "childrenV2.pageInfo.endCursor",
        ""
      );
      const networkStatus = _.get(
        getWorkbookSchoolFilteredTemplateV2,
        "networkStatus"
      );
      return {
        filteredSchoolTemplateData,
        networkStatus,
        isFetchingMoreData: _.isEqual(networkStatus, 3),
        isFilteredDataLoading: _.includes([1, 2], networkStatus),
        isFilteredData: !_.isEmpty(filteredSchoolTemplateData),
        hasNextPage: _.get(
          filteredSchoolTemplateData,
          "childrenV2.pageInfo.hasNextPage",
          false
        ),
        loadMoreEvents: prepareLoadMoreFeed({
          variables: {
            ...schoolTemplateFilters,
            after: endCursor,
          },
          queryData: getWorkbookSchoolFilteredTemplateV2,
          customPathString:
            "node.workbookTemplatesV2.workbookTemplateResource[0].childrenV2",
        }),
      };
    },
  }),
  graphql(getWorkbookTemplateQuery, {
    name: "getWorkbookTemplatesV2",
    skip: ({ enableWorkbookTemplateLibrary }) => {
      return !isNewTemplateResolverEnabled() || !enableWorkbookTemplateLibrary;
    },
    options: ({ templateFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ...templateFilters,
      },
    }),
    props({
      getWorkbookTemplatesV2: { networkStatus },
      ownProps: { isData, isLoading, templateFilters },
    }) {
      const data = getWorkbookTemplatesFromCache({
        ...templateFilters,
        folderId: "allTemplates",
      });

      //removing Basic folder which contains Blank workbook
      const templateData = _.reject(
        _.get(data, "node.workbookTemplatesV2.workbookTemplateResource", []),
        { label: "Basic" }
      );

      let isCreateBlankWorkbookAttachment = false;
      if (
        _.isEmpty(templateData) &&
        _.isEmpty(_.get(templateFilters, "searchText"))
      ) {
        isCreateBlankWorkbookAttachment = true;
      }
      return {
        templateData,
        isData: !_.isEmpty(data) && isData,
        isCreateBlankWorkbookAttachment,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getWorkbookSchoolTemplatesCountQueryV2, {
    name: "getWorkbookSchoolTemplatesCountV2",
    skip: () => !isNewTemplateResolverEnabled(),
    options: ({ organizationId, curriculumProgramId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        types: ["WORKBOOK_TEMPLATE"],
        organizationId,
        curriculumProgramId,
      },
    }),
    props({
      getWorkbookSchoolTemplatesCountV2,
      ownProps: { isFilteredData, isFilteredDataLoading },
    }) {
      const variables = _.get(getWorkbookSchoolTemplatesCountV2, "variables");
      const data = getWorkbookSchoolTemplateCountFromCache(variables);
      const totalTemplatesCount = _.get(
        data,
        "node.workbookTemplatesV2.workbookTemplateResource[0].childrenV2.resourceTypeBasedCount.totalCount",
        0
      );
      const isAnySchoolTemplateExist = !_.isEqual(totalTemplatesCount, 0);
      const networkStatus = _.get(
        getWorkbookSchoolTemplatesCountV2,
        "networkStatus"
      );
      return {
        isFilteredData: !_.isEmpty(data) && isFilteredData,
        isFilteredDataLoading:
          _.includes([1, 2], networkStatus) || isFilteredDataLoading,
        isAnySchoolTemplateExist,
      };
    },
  })
)(WorkbookTemplateV2);
